import {
  identityOperator,
  isListComparator,
  type BooleanComparator,
  type DateComparator,
  type EnumComparator,
  type Filter,
  type FilterComparator,
  type FilterDataType,
  type IdentityComparator,
  type ListFilterComparator,
  type NumericComparator,
  type SetComparator,
  type StringComparator,
} from "./FilterSchema";
import { anyIdentityTypeLabel, IDENTITY_TYPE_LABELS } from "./identityTypes";

/**
 * UI labels and a11y phrasing for the OperatorPicker's options.
 *
 * Tuple / type / predicate ownership lives in `FilterSchema`; this
 * module is the presentation layer. Keeping the map structurally
 * exhaustive (no `Partial<>`) means adding a new comparator to any
 * per-type tuple in `FilterSchema` fails to compile here until a label
 * is supplied.
 *
 * The same wire comparator can read differently depending on the field
 * type — `EQUALS` is "equals" for strings, "=" for numbers, "is" for
 * enums — so labels are keyed on `(FilterDataType, comparator)`.
 */
// The list comparators read as lists wherever they are offered, so an enum's
// "is none of" can't be read as negating each row beneath it. A boolean's one
// checklist says "is" in the picker: it has no negated list to tell apart.
const ANY_OF_LABEL = "is any of";
const NONE_OF_LABEL = "is none of";

// Numeric glyph labels, shared by the `numeric` and `metric` types (a metric
// renders identically to a numeric filter — only `Filter["type"]` differs).
// The list comparators are words: there is no glyph for set membership.
const numericLabels: Record<NumericComparator, string> = {
  EQUALS: "=",
  NOT_EQUALS: "≠",
  GREATER_THAN: ">",
  LESS_THAN: "<",
  GREATER_THAN_OR_EQUAL: "≥",
  LESS_THAN_OR_EQUAL: "≤",
  ANY_OF: ANY_OF_LABEL,
  NONE_OF: NONE_OF_LABEL,
  IS_NOT_NULL: "is set",
  IS_NULL: "is not set",
};

const COMPARATOR_LABELS: {
  string: Record<StringComparator, string>;
  numeric: Record<NumericComparator, string>;
  date: Record<DateComparator, string>;
  boolean: Record<BooleanComparator, string>;
  set: Record<SetComparator, string>;
  enum: Record<EnumComparator, string>;
  identity: Record<IdentityComparator, string>;
  metric: Record<NumericComparator, string>;
} = {
  string: {
    EQUALS: "equals",
    NOT_EQUALS: "not equals",
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
    LIKE: "like",
    NOT_LIKE: "not like",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
    ANY_OF: ANY_OF_LABEL,
    NONE_OF: NONE_OF_LABEL,
  },
  numeric: numericLabels,
  metric: numericLabels,
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
    BETWEEN: "between",
    TODAY: "today",
    LAST_3_HOURS: "last 3 hours",
    LAST_8_HOURS: "last 8 hours",
    LAST_24_HOURS: "last 24 hours",
    LAST_7_DAYS: "last 7 days",
    LAST_30_DAYS: "last 30 days",
    LAST_90_DAYS: "last 90 days",
    THIS_MONTH: "this month",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  boolean: {
    EQUALS: "equals",
    ANY_OF: "is",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  set: {
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
  },
  // Only the lists are offered; `EQUALS`, `NOT_EQUALS` and the presence
  // checks are read as lists when stored (see `upgradeLegacyFilter`).
  enum: {
    EQUALS: "is",
    NOT_EQUALS: "is not",
    ANY_OF: ANY_OF_LABEL,
    NONE_OF: NONE_OF_LABEL,
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  // An identity operator names the principal type it selects — "User" picks
  // specific users, "Any user" matches all of them. Chips read differently (see
  // `comparatorLabelForFilter`): there the operator's meaning is folded into the
  // sentence, as "Created By is Alice" / "Created By is any user".
  identity: {
    IS_USER: IDENTITY_TYPE_LABELS.user,
    IS_DEVICE: IDENTITY_TYPE_LABELS.device,
    IS_INVOCATION: IDENTITY_TYPE_LABELS.invocation,
    IS_INTEGRATION: IDENTITY_TYPE_LABELS.integration,
    IS_ORG: IDENTITY_TYPE_LABELS.org,
    IS_ANY_USER: `Any ${IDENTITY_TYPE_LABELS.user.toLowerCase()}`,
    IS_ANY_DEVICE: `Any ${IDENTITY_TYPE_LABELS.device.toLowerCase()}`,
    IS_ANY_INVOCATION: `Any ${IDENTITY_TYPE_LABELS.invocation.toLowerCase()}`,
    IS_ANY_INTEGRATION: `Any ${IDENTITY_TYPE_LABELS.integration.toLowerCase()}`,
    IS_ANY_ORG: `Any ${IDENTITY_TYPE_LABELS.org.toLowerCase()}`,
  },
};

export function comparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  // The map is exhaustive per type at the type level, but the public
  // signature accepts the umbrella `FilterComparator` so the
  // OperatorPicker can pass through without narrowing. The cast at the
  // lookup site is safe because callers iterate `comparatorsByType[type]`
  // / `presetsByType[type]`, which are guaranteed-valid for the type.
  return (COMPARATOR_LABELS[type] as Record<string, string>)[comparator];
}

/**
 * How a checklist chip reads under each list comparator, by its members
 * ("Not set" counts as one): "is" / "is not" for one member, the list wording
 * for several, and the presence wording for "Not set" alone, whose chip then
 * shows no value.
 */
const CHECKLIST_CHIP_LABELS: Record<
  ListFilterComparator,
  { one: string; several: string; notSetOnly: string }
> = {
  ANY_OF: { one: "is", several: ANY_OF_LABEL, notSetOnly: "is not set" },
  NONE_OF: {
    one: "is not",
    several: NONE_OF_LABEL,
    notSetOnly: "is set",
  },
};

/**
 * Comparator label for an active-filter chip. The comparator alone decides
 * it, the same label the OperatorPicker shows, except for the checklist
 * types and `identity`.
 *
 * An `enum` or `boolean` list reads by its members, as a sentence would:
 * "Status is Failed", "Status is any of Failed, Cancelled", and, for "Not set"
 * alone, "Status is not set" (see {@link CHECKLIST_CHIP_LABELS}).
 *
 * `identity` operators name the principal TYPE ("User", "Any device"), which reads
 * as a sentence fragment on a chip. So the type is folded into the chip's verb
 * instead — a specific pick reads "Created By is Alice" (the value segment
 * carries the name), several read "Created By is any of Alice, Bob" (sent as
 * an OR of `EQUALS`), and the valueless whole-type match reads "Created By is any
 * device" (there is no value segment to carry it).
 *
 * Display-only: the wire `comparator` is unchanged.
 */
export function comparatorLabelForFilter(filter: Filter): string {
  if (filter.type === "identity") {
    const operator = identityOperator(filter.comparator);
    if (operator?.matchesAnyOfType === true) {
      return `is ${anyIdentityTypeLabel(operator.type)}`;
    }
    return filter.values.length > 1 ? ANY_OF_LABEL : "is";
  }
  if (
    (filter.type === "enum" || filter.type === "boolean") &&
    isListComparator(filter.comparator)
  ) {
    const labels = CHECKLIST_CHIP_LABELS[filter.comparator];
    const includesNotSet = filter.includeNull === true;
    if (includesNotSet && filter.values.length === 0) {
      return labels.notSetOnly;
    }
    const members = filter.values.length + (includesNotSet ? 1 : 0);
    return members > 1 ? labels.several : labels.one;
  }
  return comparatorLabel(filter.type, filter.comparator);
}

/** The field types whose filters can be a list (`ANY_OF` / `NONE_OF`). */
export type ListMemberFieldType =
  | "string"
  | "numeric"
  | "metric"
  | "enum"
  | "boolean";

/** The comparator pills a list filter's members are drawn with. */
export interface ListMemberLabels {
  /** Drawn before each value: the type's `EQUALS` label ("equals", "=", "is"). */
  value: string;
  /** The "Not set" member, which is this pill alone: the type's `IS_NULL` label. */
  notSet: string;
}

/**
 * How each member of a list filter (`ANY_OF` / `NONE_OF`) is labelled in the
 * value editor: by the comparator it stands for, worded as the operator picker
 * words it on this field type. A value is matched by equality and "Not set" by
 * absence, so a value reads "equals nightly" and "Not set" reads "is not set".
 *
 * The members keep these under `NONE_OF` too. "is none of" negates the list as
 * a whole and stays on screen beside it; flipping each member to "not equals"
 * / "is set" would read as a double negative under it.
 */
export function listMemberLabels(type: ListMemberFieldType): ListMemberLabels {
  return {
    value: comparatorLabel(type, "EQUALS"),
    notSet: comparatorLabel(type, "IS_NULL"),
  };
}

/** A notation a comparator reads its value in, for a form to explain. */
export type ValueSyntax = "like-pattern";

/**
 * The notation `comparator` reads its value in, or null for a value taken
 * literally. A tag, not copy: the form showing the value words it.
 */
export function valueSyntax(
  type: FilterDataType,
  comparator: FilterComparator,
): ValueSyntax | null {
  const isLike = comparator === "LIKE" || comparator === "NOT_LIKE";
  return type === "string" && isLike ? "like-pattern" : null;
}

/**
 * Per-(type, comparator) spoken-label overrides. Most types' visual
 * labels are already phrases that read fine — only date diverges
 * (visual "before" for the `LESS_THAN` wire comparator, etc.). Numeric
 * glyphs can't be spoken, so the fallback to the underscored comparator
 * key ("LESS_THAN" → "less than") is exactly right for them. The list
 * comparators are spoken as their options read, since the fallback's bare
 * "any of" / "none of" is not what any option says.
 */
const LIST_SPOKEN_LABELS: Partial<Record<FilterComparator, string>> = {
  ANY_OF: ANY_OF_LABEL,
  NONE_OF: NONE_OF_LABEL,
};

const SPOKEN_LABEL_OVERRIDES: Partial<
  Record<FilterDataType, Partial<Record<FilterComparator, string>>>
> = {
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
  },
  string: LIST_SPOKEN_LABELS,
  enum: LIST_SPOKEN_LABELS,
  boolean: { ANY_OF: "is" },
  numeric: LIST_SPOKEN_LABELS,
  metric: LIST_SPOKEN_LABELS,
};

/**
 * Screen-reader-spoken label for a comparator within a field type.
 * Numeric operators render as glyphs ("=", "≠", "≤", …) which screen
 * readers narrate inconsistently across vendors — providing an explicit
 * phrase form gets us deterministic announcement. Date operators display
 * words ("before", "after") that don't match the wire comparator key,
 * so we override there too. Falls back to the comparator key with
 * underscores swapped for spaces ("GREATER_THAN_OR_EQUAL" → "greater
 * than or equal").
 */
export function spokenComparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  return (
    SPOKEN_LABEL_OVERRIDES[type]?.[comparator] ??
    comparator.toLowerCase().replace(/_/g, " ")
  );
}
